import { Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { getStateConfig, stateConfig } from './stateConfig';

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const compareByHour = (a, b) => {
  if (!a.hora_inicio && !b.hora_inicio) return 0;
  if (!a.hora_inicio) return -1;
  if (!b.hora_inicio) return 1;
  return a.hora_inicio.localeCompare(b.hora_inicio);
};

const MantencionCard = ({ item }) => {
  const cfg = getStateConfig(item.status);
  return (
    <Tooltip
      title={
        <div className="space-y-1">
          <div className="font-semibold text-white">{item.Cliente?.name}</div>
          <div className="text-gray-200 text-xs">
            {cfg.label}
            {item.hora_inicio ? ` · ${item.hora_inicio.slice(0, 5)}` : ''}
          </div>
          {item.Specialist?.fullname && (
            <div className="flex items-center text-gray-200 text-xs">
              <UserOutlined className="mr-1" />
              {item.Specialist.fullname}
            </div>
          )}
        </div>
      }
      placement="top"
    >
      <div
        className="mantencion-card rounded cursor-pointer transition-shadow hover:shadow-sm px-2 py-1"
        style={{
          borderLeft: `3px solid ${cfg.color}`,
          backgroundColor: cfg.bgColor,
          color: cfg.textColor,
        }}
      >
        <div className="flex items-center justify-between gap-1">
          <span
            className="inline-block rounded-full flex-shrink-0"
            style={{ width: 6, height: 6, background: cfg.color }}
          />
          {item.hora_inicio && (
            <span
              className="text-[10px] bg-white rounded px-1 text-gray-600 flex-shrink-0"
              style={{ fontWeight: 500 }}
            >
              {item.hora_inicio.slice(0, 5)}
            </span>
          )}
        </div>
        <div className="text-xs font-medium truncate mt-0.5">{item.Cliente?.name}</div>
        {item.Specialist?.fullname && (
          <div className="text-[10px] text-gray-500 truncate">{item.Specialist.fullname}</div>
        )}
      </div>
    </Tooltip>
  );
};

const DayColumn = ({ day, isSelected, events, onSelectDay }) => {
  const sorted = [...events].sort(compareByHour);
  const headerCls = isSelected
    ? 'border-b pb-2 mb-2 text-center'
    : 'border-b pb-2 mb-2 text-center';
  const containerCls = isSelected
    ? 'flex flex-col border-2 rounded-lg p-2'
    : 'flex flex-col border rounded-lg p-2 bg-gray-50';
  const containerStyle = isSelected ? { borderColor: '#3b82f6', backgroundColor: '#eff6ff' } : {};
  const dayLabelCls = isSelected
    ? 'text-[11px] uppercase tracking-wider font-semibold'
    : 'text-[11px] uppercase tracking-wider font-semibold text-gray-500';
  const dayLabelStyle = isSelected ? { color: '#2563eb' } : {};
  const dayNumberCls = isSelected ? 'text-lg font-semibold' : 'text-lg font-semibold text-gray-800';
  const dayNumberStyle = isSelected ? { color: '#1e40af' } : {};
  const countCls = isSelected
    ? 'text-[10px] font-medium mt-0.5'
    : events.length > 0
    ? 'text-[10px] text-gray-500 mt-0.5'
    : 'text-[10px] text-gray-400 mt-0.5';
  const countStyle = isSelected ? { color: '#2563eb' } : {};

  return (
    <div className={containerCls} style={containerStyle}>
      <button
        type="button"
        onClick={() => onSelectDay(day)}
        className={headerCls}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
      >
        <div className={dayLabelCls} style={dayLabelStyle}>
          {DAY_LABELS[day.isoWeekday() - 1]}
        </div>
        <div className={dayNumberCls} style={dayNumberStyle}>
          {day.date()}
        </div>
        <div className={countCls} style={countStyle}>
          {events.length > 0 ? `${events.length} mant.` : '—'}
        </div>
      </button>

      <div className="space-y-1.5 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
        {sorted.length === 0 && (
          <div className="flex-1 flex items-center justify-center h-full">
            <span className="text-gray-300 text-xs">sin eventos</span>
          </div>
        )}
        {sorted.map((item) => (
          <div key={item.id} onClick={() => onSelectDay(day)}>
            <MantencionCard item={item} />
          </div>
        ))}
      </div>
    </div>
  );
};

const WeekCalendar = ({ weekStart, selectedDay, onSelectDay, data }) => {
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
  const selectedKey = selectedDay?.format('YYYY-MM-DD');

  const eventsByDay = days.map((day) => {
    const key = day.format('YYYY-MM-DD');
    return (data || []).filter((item) => item.fecha_asignacion === key);
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div
        className="grid grid-cols-7 gap-2"
        style={{ minHeight: 480 }}
        data-testid="week-calendar-grid"
      >
        {days.map((day, i) => (
          <DayColumn
            key={day.format('YYYY-MM-DD')}
            day={day}
            isSelected={day.format('YYYY-MM-DD') === selectedKey}
            events={eventsByDay[i]}
            onSelectDay={onSelectDay}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t text-xs">
        {Object.entries(stateConfig).map(([status, cfg]) => (
          <div key={status} className="flex items-center gap-1.5">
            <span
              className="inline-block rounded-full"
              style={{ width: 8, height: 8, background: cfg.color }}
            />
            <span className="text-gray-600">{cfg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekCalendar;
